import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Users, ShoppingBag, LayoutGrid, Home, Scissors, Sparkles, ArrowLeft, User, CalendarDays } from "lucide-react";
import { useAuth } from "@/lib/AuthProvider";
import { TEST_IDS } from "@/constants/testIds";
import Brand from "@/components/Brand";
import ProfileCompletionReminder from "@/components/ProfileCompletionReminder";
import SophieAgent from "@/components/SophieAgent";

// Fixed bottom quick-access bar — 5 items with slightly smaller icons.
const BOTTOM_NAV = [
  { to: "/community", label: "Social", icon: Users,       tid: "bottom-nav-community" },
  { to: "/shop",      label: "Shop",      icon: ShoppingBag, tid: "bottom-nav-shop" },
  { to: "/beauty",    label: "Beauty",    icon: Sparkles,    tid: "bottom-nav-beauty" },
  { to: "/stylists",  label: "Stylists",  icon: Scissors,    tid: "bottom-nav-stylists" },
  { to: "/tools",     label: "Tools",     icon: LayoutGrid,  tid: "bottom-nav-tools" },
];

// Routes that should NOT show the back button (roots / bottom-nav destinations).
const BACK_HIDDEN = new Set(["/", "/home", "/community", "/shop", "/beauty", "/stylists", "/tools", "/profile", "/login", "/signup", "/onboarding"]);

export default function Layout({ children }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const showBack = !BACK_HIDDEN.has(location.pathname);
  const isHome = location.pathname === "/" || location.pathname === "/home";

  return (
    <div className="min-h-screen flex flex-col">
      <ProfileCompletionReminder />
      {/*
        HEADER — Calendar-or-Home / Back (left) · Brand (center) · Profile (right).
        We use `fixed` (not sticky) so the header always stays pinned even when
        the page's scroll container isn't the window (e.g. some modals). The
        matching top spacer below reserves the header height so content isn't
        hidden underneath.
        env(safe-area-inset-top) keeps it clear of the Android status bar /
        camera notch / battery indicator on edge-to-edge devices.
      */}
      <header
        className="fixed top-0 left-0 right-0 z-40 glass-surface border-b border-border/40"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 h-14 grid grid-cols-[auto_1fr_auto] items-center gap-2">
          {/* LEFT — Back on inner pages, Calendar on Home, Home button elsewhere */}
          <div className="flex items-center gap-1 justify-self-start">
            {showBack ? (
              <button
                type="button"
                onClick={() => navigate(-1)}
                data-testid="header-back-btn"
                aria-label="Back"
                className="h-10 w-10 inline-flex items-center justify-center hover:opacity-70 transition-opacity"
              >
                <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
              </button>
            ) : isHome ? (
              // On the Home page we replace the Home icon with a Calendar
              // shortcut — the user's #1 request. Home is already implicit
              // (they're on it) so the slot is free for a productivity CTA.
              <NavLink
                to="/calendar"
                data-testid="header-calendar"
                aria-label="Calendar"
                className={({ isActive }) =>
                  `h-10 w-10 inline-flex items-center justify-center hover:opacity-70 transition-opacity ${
                    isActive ? "text-accent" : ""
                  }`
                }
              >
                <CalendarDays className="w-5 h-5" strokeWidth={1.7} />
              </NavLink>
            ) : (
              <NavLink
                to="/home"
                data-testid="header-home"
                aria-label="Home"
                end
                className={({ isActive }) =>
                  `h-10 w-10 inline-flex items-center justify-center hover:opacity-70 transition-opacity ${
                    isActive ? "text-accent" : ""
                  }`
                }
              >
                <Home className="w-5 h-5" strokeWidth={1.7} />
              </NavLink>
            )}
          </div>

          {/* CENTER — Brand, always centred */}
          <Link
            to="/home"
            data-testid="header-brand"
            className="justify-self-center inline-flex items-baseline gap-1.5"
          >
            <Brand size="md" />
          </Link>

          {/* RIGHT — Profile (generic user icon, tap opens Profile page) */}
          <div className="flex items-center gap-1 justify-self-end">
            <NavLink
              to="/profile"
              data-testid={TEST_IDS.nav.profile}
              aria-label="Profile"
              className={({ isActive }) =>
                `h-10 w-10 inline-flex items-center justify-center hover:opacity-70 transition-opacity ${
                  isActive ? "text-accent" : ""
                }`
              }
            >
              <User className="w-5 h-5" strokeWidth={1.7} />
            </NavLink>
          </div>
        </div>
      </header>

      {/* Header height spacer — matches h-14 (56px) + safe-area-inset-top.
          Without this, `fixed` header overlaps the first row of content. */}
      <div
        aria-hidden="true"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <div className="h-14" />
      </div>

      <main className="flex-1 pb-24">{children}</main>

      {/*
        BOTTOM NAV — 5 essentials with active teal pill. Uses
        env(safe-area-inset-bottom) so it sits above Android 10+ gesture-nav
        bar / iOS home-indicator area.
      */}
      <nav
        className="fixed bottom-0 inset-x-0 z-50 bg-background border-t border-border shadow-[0_-1px_12px_rgba(0,0,0,0.06)]"
        data-testid="bottom-nav"
        aria-label="Quick access"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <ul className="max-w-7xl mx-auto grid grid-cols-5 px-2 py-1.5 gap-1">
          {BOTTOM_NAV.map((b) => (
            <li key={b.to} className="min-w-0">
              <NavLink
                to={b.to}
                data-testid={b.tid}
                className={({ isActive }) =>
                  `relative flex flex-col items-center justify-center gap-1 py-2.5 rounded-2xl transition-colors duration-200 ${
                    isActive
                      ? "bg-accent text-accent-foreground shadow-[0_4px_14px_-2px_rgba(0,102,102,0.45)]"
                      : "text-foreground/70 hover:text-accent hover:bg-accent/10"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <b.icon
                      size={18}
                      className="sm:!w-[17px] sm:!h-[17px]"
                      strokeWidth={isActive ? 2.2 : 1.7}
                    />
                    <span
                      className={`text-[10px] sm:text-[9px] uppercase tracking-[0.12em] sm:tracking-[0.14em] ${
                        isActive ? "font-semibold" : "font-medium"
                      }`}
                    >
                      {b.label}
                    </span>
                  </>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      <SophieAgent />
    </div>
  );
}
